import { persistedValue } from '@/lib/api/client'

import { type MarketSearch, marketSearchSchema } from './market-search'

/**
 * The Market's last search, kept in this browser so leaving the Market (a listing, a car, another
 * screen) and coming back finds it as it was. Only this viewer's, and only a convenience: storage
 * may be unavailable, and then the Market opens unfiltered as it always did.
 */
const searchStore = persistedValue('damis.market.search')

/** What was last typed into the plain-language search, shown again in its box. */
export const askStore = persistedValue('damis.market.ask')

/** The search with only what it sets: nothing left is no search at all. */
function setOnly(search: MarketSearch): Partial<MarketSearch> {
  return Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined),
  ) as Partial<MarketSearch>
}

export function isEmptySearch(search: MarketSearch): boolean {
  return Object.keys(setOnly(search)).length === 0
}

/** Keep `search` as the last; an empty one (Clear) forgets it. */
export function rememberSearch(search: MarketSearch): void {
  const kept = setOnly(search)
  searchStore.set(Object.keys(kept).length === 0 ? null : JSON.stringify(kept))
}

/** The last search kept, read as the address would be; `undefined` when there is none. */
export function rememberedSearch(): MarketSearch | undefined {
  const raw = searchStore.get()
  if (raw === null) return undefined
  try {
    const search = marketSearchSchema.parse(JSON.parse(raw))
    return isEmptySearch(search) ? undefined : search
  } catch {
    return undefined
  }
}
